'use client';

import { ChangeEvent, FC, KeyboardEvent, Ref, useEffect, useState } from 'react';

import classNames from 'classnames';

import { useAriaErrorMessageId } from '../../../../hooks/useAriaErrorMessageId/useAriaErrorMessageId';
import { useControlledValue } from '../../../../hooks/useControlledValue/useControlledValue';
import { useForwardedRef } from '../../../../hooks/useForwardedRef/useForwardedRef';
import useId from '../../../../hooks/useId/useId';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   inputRef?: Ref<HTMLInputElement>;
   id?: string;
   name?: string;
   autocomplete?: 'on' | 'off';
   textSize?: 'small' | 'medium';
   hideStepper?: boolean;
   weakSuffix?: boolean;
   clearable?: boolean;
   disabled?: boolean;
   error?: boolean;
   errorMessage?: string;
   supportText?: string;
   label?: string;
   leadingIcon?: string;
   max?: number;
   maxLength?: number;
   min?: number;
   readOnly?: boolean;
   required?: boolean;
   step?: number;
   suffix?: string;
   trailingIcon?: string;
   placeHolder?: string;
   type?: 'email' | 'number' | 'password' | 'text';
   value?: string;
   defaultValue?: string;
   width?: string | number;
   autoFocus?: boolean;
   onClear?: () => void;
   onChange?: (value: string) => void;
   onEnter?: (value: string) => void;
   onFocus?: () => void;
   onBlur?: () => void;
   onTrailingIconClick?: () => void;
   onSuffixClick?: () => void;
   ariaLabel?: string;
}

export const InputField: FC<Props> = ({
   inputRef: refFromProps,
   id: idFromProps,
   name,
   autocomplete = 'on',
   clearable = false,
   disabled = false,
   error = false,
   errorMessage,
   supportText,
   textSize = 'medium',
   hideStepper,
   weakSuffix,
   label,
   leadingIcon,
   max,
   maxLength = 255,
   min,
   readOnly = false,
   required = false,
   step = 1,
   suffix,
   trailingIcon,
   type = 'text',
   value: valueFromProps,
   defaultValue,
   placeHolder,
   width,
   autoFocus,
   onClear,
   onChange,
   onEnter,
   onFocus,
   onBlur,
   onTrailingIconClick,
   onSuffixClick,
   ariaLabel
}) => {
   const id = useId(idFromProps, 'input-field');
   const errorMessageId = useAriaErrorMessageId(error, 'input-field');

   const [value, setValue] = useControlledValue(valueFromProps, onChange, defaultValue || '');

   const [focused, setFocused] = useState(false);

   const inputRef = useForwardedRef(refFromProps);

   /* istanbul ignore next */
   const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
   };

   const handleClear = () => {
      setValue('');
      onClear?.();
   };

   const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
         onEnter?.(value);
      }
   };

   const handleFocus = () => {
      setFocused(true);
      onFocus?.();
   };

   useEffect(() => {
      if (type === 'number') {
         let newValue = parseFloat(value);
         if (Number.isNaN(newValue)) return;

         if (min !== undefined && newValue < min) newValue = min;
         if (max !== undefined && newValue > max) newValue = max;
         if (newValue.toString() !== value) {
            setValue(newValue.toString());
            onChange?.(newValue.toString());
         }
      }
   }, [min, max]);

   const handleBlur = () => {
      setFocused(false);
      onBlur?.();

      if (type === 'number' && value) {
         const numberValue = Number(value);

         if (min !== undefined && numberValue < min) {
            setValue(min.toString());
         }
         if (max !== undefined && numberValue > max) {
            setValue(max.toString());
         }
      }
   };

   return (
      <div data-testid={`input-field${error ? '--error' : ''}`} style={{ width }} className="gap-component-input-input-field-container-spacing-outer-gap flex w-full flex-col">
         <div className={`group relative cursor-text ${disabled ? 'pointer-events-none' : ''}`}>
            {label && (
               <label
                  data-testid="input-field-label"
                  htmlFor={id}
                  style={{ maxWidth: 'calc(100% - (var(--component-input-input-field-container-spacing-padding-x-no-icon) * 2))' }}
                  className={`absolute left-0 top-0 flex items-center transition-all cursor-pointer
                           ${!error && !disabled && (value !== '' || focused) ? 'text-component-input-input-field-label-color-text-neutral-enabled-as-label' : ''}
                           ${!error && !disabled && focused ? 'text-component-input-input-field-label-color-text-neutral-focused' : ''}
                           ${!error && !disabled && value === '' && !focused ? 'text-component-input-input-field-label-color-text-neutral-enabled-as-placeholder' : ''}
                           ${disabled ? 'text-component-input-input-field-label-color-text-neutral-disabled' : ''}
                           ${error && !disabled ? 'text-component-input-input-field-label-color-text-error-enabled group-hover:text-component-input-input-field-label-color-text-error-hover' : ''}
                           ${focused ? 'h-[var(--component-input-input-field-container-border-width-focused)]' : ''}
                           ${value !== '' && !focused ? 'h-[var(--component-input-input-field-container-border-width-enabled)]' : ''}
                           ${!focused && value === '' ? 'h-full' : ''}
                           ${value !== '' || focused ? 'bg-component-input-input-field-container-color-background px-component-input-input-field-label-spacing-padding-x' : ''}
                           ${
                              value === '' && !focused && leadingIcon
                                 ? 'phone:ml-[calc(var(--component-input-input-field-container-spacing-padding-x-icon)+var(--component-input-input-field-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-mobile-body))] tab-port:ml-[calc(var(--component-input-input-field-container-spacing-padding-x-icon)+var(--component-input-input-field-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] tab-land:ml-[calc(var(--component-input-input-field-container-spacing-padding-x-icon)+var(--component-input-input-field-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] ml-[calc(var(--component-input-input-field-container-spacing-padding-x-icon)+var(--component-input-input-field-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-desktop-body))]'
                                 : 'ml-component-input-input-field-container-spacing-padding-x-top-label'
                           }
                      `.replace(/\s+/g, ' ')}
               >
                  <Typography variant={value !== '' || focused ? 'caption' : 'body'} color="inherit" maxLines="1">
                     {label}
                     {required && '*'}
                  </Typography>
               </label>
            )}

            <div
               data-testid="input-field-container"
               onClick={() => inputRef.current?.focus()}
               role="presentation"
               className={`min-h-component-input-input-field-container-sizing-min-height py-component-input-input-field-container-spacing-padding-y gap-component-input-input-field-container-spacing-gap bg-component-input-input-field-container-color-background rounded-component-input-input-field-container-border-radius border-component-input-input-field-container-border-width-enabled focus-within:!border-component-input-input-field-container-border-width-focused box-border flex items-start justify-between transition-all
                           ${
                              error && !disabled
                                 ? '!border-component-input-input-field-container-color-border-error-enabled hover:!border-component-input-input-field-container-color-border-error-hover focus-within:!border-component-input-input-field-container-color-border-error-enabled'
                                 : '!border-component-input-input-field-container-color-border-neutral-enabled hover:!border-component-input-input-field-container-color-border-neutral-hover focus-within:!border-component-input-input-field-container-color-border-neutral-focused'
                           }
                           ${leadingIcon ? 'pl-component-input-input-field-container-spacing-padding-x-icon' : 'pl-component-input-input-field-container-spacing-padding-x-no-icon'}
                           ${trailingIcon ? 'pr-component-input-input-field-container-spacing-padding-x-icon' : 'pr-component-input-input-field-container-spacing-padding-x-no-icon'}
                  `.replace(/\s+/g, ' ')}
            >
               {leadingIcon && (
                  <div
                     data-testid="input-field-leading-icon"
                     className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                        disabled ? 'text-component-input-input-field-icon-start-color-text-on-surface-disabled' : 'text-component-input-input-field-icon-start-color-text-on-surface-enabled'
                     }`.replace(/\s+/g, ' ')}
                  >
                     <Icon icon={leadingIcon} />
                  </div>
               )}
               <div className="flex h-full flex-1 items-center">
                  <input
                     data-testid="input-field-input"
                     autoComplete={autocomplete}
                     className={classNames(
                        'text-component-input-input-field-input-color-text-on-surface-enabled disabled:text-component-input-input-field-input-color-text-on-surface-disabled w-full bg-transparent outline-none',
                        hideStepper && 'hide-number-stepper',
                        textSize === 'small' && 'typography-component-data-display-typography-desktop-label-regular',
                        textSize === 'medium' && 'typography-component-data-display-typography-desktop-body-regular'
                     )}
                     disabled={disabled}
                     id={id}
                     max={max}
                     maxLength={maxLength}
                     min={min}
                     name={name}
                     onBlur={handleBlur}
                     onChange={handleChange}
                     onFocus={handleFocus}
                     onKeyUp={handleKeyPress}
                     readOnly={readOnly}
                     ref={inputRef}
                     required={required}
                     step={step}
                     type={type}
                     value={value}
                     aria-invalid={error}
                     aria-errormessage={errorMessageId}
                     aria-label={ariaLabel}
                     autoFocus={autoFocus}
                     placeholder={placeHolder}
                  />
               </div>
               {suffix && (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                     {...(onSuffixClick ? { role: 'button' } : {})}
                     data-testid="input-field-suffix"
                     className={classNames('flex items-center', disabled && 'text-component-input-input-field-suffix-color-text-disabled', !disabled && 'text-component-input-input-field-suffix-color-text-enabled', onSuffixClick && 'cursor-pointer')}
                     onClick={onSuffixClick}
                  >
                     <Typography variant={textSize === 'small' ? 'small' : 'body'} color={weakSuffix ? 'weak' : 'inherit'}>
                        {suffix}
                     </Typography>
                  </div>
               )}
               {clearable && value !== '' && (
                  <div
                     data-testid="input-field-clear"
                     onClick={handleClear}
                     role="presentation"
                     className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center cursor-pointer ${
                        disabled ? 'text-component-input-search-icon-end-color-text-on-surface-disabled' : 'text-component-input-search-icon-end-color-text-on-surface-enabled'
                     }`.replace(/\s+/g, ' ')}
                  >
                     <Icon icon="times-circle" />
                  </div>
               )}
               {trailingIcon && (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                     {...(onTrailingIconClick ? { role: 'button' } : {})}
                     data-testid="input-field-trailing-icon"
                     className={classNames(
                        'w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center',
                        disabled && 'text-component-input-input-field-icon-end-color-text-on-surface-disabled',
                        !disabled && 'text-component-input-input-field-icon-end-color-text-on-surface-enabled',
                        onTrailingIconClick && 'cursor-pointer'
                     )}
                     onClick={onTrailingIconClick}
                  >
                     <Icon icon={trailingIcon} />
                  </div>
               )}
            </div>
         </div>
         {supportText && (
            <div
               data-testid="input-field-support-text"
               className={`pl-component-input-input-field-supporting-text-spacing-padding-left gap-component-input-input-field-supporting-text-spacing-gap flex items-center

                        ${!disabled ? 'text-component-input-input-field-supporting-text-color-text-neutral-enabled' : ''}
                        ${disabled ? 'text-component-input-input-field-supporting-text-color-text-neutral-disabled' : ''}
                        `.replace(/\s+/g, ' ')}
            >
               {supportText && (
                  <Typography variant="caption" color="inherit">
                     {supportText}
                  </Typography>
               )}
            </div>
         )}
         {error && errorMessage && (
            <div data-testid="input-field-error-message" className="pl-component-input-input-field-supporting-text-spacing-padding-left gap-component-input-input-field-supporting-text-spacing-gap text-component-input-input-field-supporting-text-color-text-error flex items-center">
               <Icon icon="exclamation-circle" size="caption" />
               <Typography id={errorMessageId} variant="caption" color="inherit">
                  {errorMessage}
               </Typography>
            </div>
         )}
      </div>
   );
};
